import { useQuery, useQueryClient } from '@tanstack/react-query';
import { checkoutIntentsClient } from 'app/checkout-intents/client';

type BillingResponse = {
  drawdown: {
    enabled: boolean;
    config: {
      targetBalanceSubunits: number;
      minBalanceSubunits: number;
      currency: string;
      chargeAutomatically: boolean;
    } | null;
    balance: {
      amountSubunits: number;
      currencyCode: string;
    } | null;
  };
};

const fetchBilling = async (): Promise<BillingResponse> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  return client.get<BillingResponse>('/api/v1/billing');
};

export const useBilling = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['billing'],
    queryFn: fetchBilling,
    retry: 1,
  });

  return {
    drawdown: data?.drawdown ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};

type SetupDrawdownParams = {
  targetBalanceSubunits: number;
  minBalanceSubunits: number;
  chargeAutomatically?: boolean;
};

export const useSetupDrawdown = () => {
  const queryClient = useQueryClient();

  const setupDrawdown = async (params: SetupDrawdownParams): Promise<BillingResponse> => {
    const client = checkoutIntentsClient.get();
    if (!client) {
      throw new Error('API client not initialized');
    }

    const result = await client.post<BillingResponse>('/api/v1/billing/drawdown', {
      body: params,
    });

    // Update cache immediately with the response
    queryClient.setQueryData(['billing'], result);
    // Also invalidate the old balance query
    queryClient.invalidateQueries({ queryKey: ['drawdown-balance'] });

    return result;
  };

  return { setupDrawdown };
};
